// frontend/src/hooks/useApprovalWorkflowData.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { configAPI } from '../services/api';
import toast from '../utils/toast';

// ─── Workflow Templates ─────────────────────────────────────────────────

export const useApprovalWorkflows = (params = {}) => {
  return useQuery({
    queryKey: ['approval-workflows', 'list', params],
    queryFn: async () => {
      const response = await configAPI.getLeaveApprovalWorkflows(params);
      return response.data?.results || response.data || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useActiveWorkflows = () => {
  return useQuery({
    queryKey: ['approval-workflows', 'active'],
    queryFn: async () => {
      const response = await configAPI.getActiveWorkflows();
      return response.data?.results || response.data || [];
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useWorkflowVersionHistory = (workflowId) => {
  return useQuery({
    queryKey: ['approval-workflows', 'versions', workflowId],
    queryFn: async () => {
      const response = await configAPI.getWorkflowVersionHistory(workflowId);
      return response.data?.results || response.data || [];
    },
    enabled: !!workflowId,
    staleTime: 2 * 60 * 1000,
  });
};

export const useWorkflowSummary = () => {
  return useQuery({
    queryKey: ['approval-workflows', 'summary'],
    queryFn: async () => {
      const response = await configAPI.getWorkflowSummary();
      return response.data;
    },
    staleTime: 2 * 60 * 1000,
  });
};

export const useCreateWorkflow = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => configAPI.createLeaveApprovalWorkflow(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['approval-workflows'] });
      toast.success('Workflow created successfully');
    },
    onError: (error) => {
      const msg = error.response?.data?.detail || error.response?.data?.non_field_errors?.[0] || 'Failed to create workflow';
      toast.error(msg);
    },
  });
};

export const useUpdateWorkflow = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => configAPI.updateLeaveApprovalWorkflow(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['approval-workflows'] });
      toast.success('Workflow updated successfully');
    },
    onError: (error) => {
      const msg = error.response?.data?.detail || error.response?.data?.[0] || 'Failed to update workflow — it may be in use';
      toast.error(msg);
    },
  });
};

export const useDeleteWorkflow = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => configAPI.deleteLeaveApprovalWorkflow(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['approval-workflows'] });
      toast.success('Workflow deleted');
    },
    onError: () => {
      toast.error('Failed to delete workflow');
    },
  });
};

export const useDuplicateWorkflow = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => configAPI.duplicateWorkflow(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['approval-workflows'] });
      toast.success('New version created successfully');
    },
    onError: (error) => {
      const msg = error.response?.data?.error || 'Failed to create new version';
      toast.error(msg);
    },
  });
};

// ─── Workflow Assignments ───────────────────────────────────────────────

export const useWorkflowAssignments = (params = {}) => {
  return useQuery({
    queryKey: ['workflow-assignments', 'list', params],
    queryFn: async () => {
      const response = await configAPI.getWorkflowAssignments(params);
      return response.data?.results || response.data || [];
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateAssignment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => configAPI.createWorkflowAssignment(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow-assignments'] });
      toast.success('Assignment created successfully');
    },
    onError: (error) => {
      const msg = error.response?.data?.detail || error.response?.data?.non_field_errors?.[0] || 'Failed to create assignment';
      toast.error(msg);
    },
  });
};

export const useUpdateAssignment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => configAPI.updateWorkflowAssignment(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow-assignments'] });
      toast.success('Assignment updated');
    },
    onError: () => {
      toast.error('Failed to update assignment');
    },
  });
};

export const useDeleteAssignment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => configAPI.deleteWorkflowAssignment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow-assignments'] });
      toast.success('Assignment removed');
    },
    onError: () => {
      toast.error('Failed to remove assignment');
    },
  });
};

// ─── Employee Preview ───────────────────────────────────────────────────

export const usePreviewWorkflowForEmployee = () => {
  return useMutation({
    mutationFn: (staffId) => configAPI.previewWorkflowForEmployee(staffId),
    onError: (error) => {
      const msg = error.response?.data?.error || 'Failed to preview workflow';
      toast.error(msg);
    },
  });
};

export const useBulkPreview = () => {
  return useMutation({
    mutationFn: (staffIds) => configAPI.bulkPreviewAssignments(staffIds),
    onError: () => {
      toast.error('Failed to run bulk preview');
    },
  });
};
